/**
 * ImfuyoAcademy.jsx
 * Client-facing landing + access page.
 *
 * IMPORTANT — account model change:
 *   Accounts are created by Imfuyo staff in the Manager dashboard, NOT here.
 *   A client never signs up on this page. Staff create an account (full name
 *   + phone) which generates a unique access token, and hand that token to
 *   the client. This page's only job is: take that token, look it up, and
 *   drop the client into their course.
 *
 * Firestore collection: imfuyo_academy
 * Document ID: the access token itself (e.g. "IMF-4K7X-2Q9P")
 * Fields:
 *   fullName      : string
 *   phone         : string
 *   programmes    : string[]              (e.g. ["mbuziplus"])
 *   paymentStatus : 'paid' | 'unpaid'
 *   enrolledAt    : Timestamp
 *   lastLogin     : Timestamp
 *
 * No PIN, no phone/PIN login — the access token is the only credential.
 */

import { useState, useEffect, useRef } from "react";
import {
  ChevronRight, CheckCircle, GraduationCap, X, KeyRound,
  ArrowRight, BookOpen, Play, Leaf, Lock, Check,
  Users, Award, Clock, Zap, Star, LogOut, BookMarked, HelpCircle,
  ChevronLeft, TrendingUp, Target, CreditCard, AlertCircle
} from "lucide-react";

// ─── Firebase ──────────────────────────────────────────────
import { db } from "../Libs/firebase-config.mjs";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs } from "firebase/firestore";

// ─── Course screens ───────────────────────────────────────
import MbuziplusCourse from "./ImfuyoAcademy/Mbuzipluscourse.jsx";
import AcademyTeaser from "./ImfuyoAcademy/AcademyTeaser.jsx";
import AcademyLanding, { AcademyJoin, openRegister, NOTIFY_URL } from "./ImfuyoAcademy/AcademyLanding.jsx";

/* ─── DATA ─────────────────────────────────────────────────── */
const PROGRAMS = [
  {
    id: "mbuziplus",
    tag: "Open now",
    title: "MbuziPlus Course",
    subtitle: "by Imfuyo Academy",
    description:
      "The whole dairy goat value chain in 8 parts and 42 modules: choosing and caring for goats, feed, health, breeding, milk, money and market. Free preview. Finish with a Certificate of Completion.",
    image: "/academy/academy-poster.png",
    modules: ["Start here", "Choose your goats", "Feed and water", "Health", "Breeding and kidding", "Milk and products", "Money and records", "Market and growth"],
    facts: [["42", "modules"], ["172", "study hours"], ["1", "certificate"]],
    status: "enrolling",
    accent: "#3E7A32",
    accentDark: "#285020",
  },
  {
    id: "youth-agritech",
    tag: "Coming soon",
    title: "Imfuyo Youth Agri-Tech",
    subtitle: "A future Imfuyo Academy programme",
    description:
      "A programme for young people who want to bring data and technology into farming. Dates, content and fees will be announced here and by Imfuyo when they are ready.",
    image: "",
    modules: [],
    facts: [],
    status: "coming-soon",
    accent: "#3b82f6",
    accentDark: "#2563eb",
  },
];

// ─── PROJECTED STATS (aspirational targets) ────────────────
const STATS = [
  { icon: GraduationCap, label: "Programmes planned", value: "5+", projection: "Launching 5+ programmes by 2027" },
  { icon: Users,        label: "Learner target", value: "10,000+", projection: "Reaching 10,000+ farmers across Kenya" },
  { icon: Award,        label: "Graduate target", value: "5,000+", projection: "5,000+ certified graduates by 2028" },
  { icon: Star,         label: "Income goal", value: "85%+", projection: "Our goal: 85%+ increase in farm income" },
];

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Register and pay in the Marketplace",
    body: "Pay the training fee of Kenya Shillings 4,900 on the secure Paystack page, with M-Pesa, card or bank. It is the same fee online or in class, with no other cost.",
  },
  {
    n: "02",
    title: "Get your access code",
    body: "Our team sends your personal access code on WhatsApp, usually within 12 to 24 hours, with the dates of the next training. Keep it safe: it is your key into the course.",
  },
  {
    n: "03",
    title: "Enter your code",
    body: "Come back here, enter your access code, and you're straight into your programme. No password to remember.",
  },
];

/* ─── ACCESS CODE NORMALISATION ──────────────────────────────
   Forgiving of case, stray spaces, or missing dashes — reformats
   back into the canonical IMF-XXXX-XXXX shape wherever possible
   so a client typing "imf4k7x2q9p" still resolves correctly.   */
function normaliseAccessCode(raw) {
  const cleaned = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (cleaned.startsWith("IMF") && cleaned.length === 11) {
    const rest = cleaned.slice(3);
    return `IMF-${rest.slice(0, 4)}-${rest.slice(4, 8)}`;
  }
  return raw.trim().toUpperCase();
}

/* ─── DEVICE BINDING ─────────────────────────────────────────
   Each access code is for one person. On first use we remember
   this phone or computer (a random id kept in the browser) on the
   account, and later refuse the code from a different device.
   Staff can reset the device from the admin site.               */
const DEVICE_KEY = "imfuyo_academy_device";
function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      const buf = new Uint8Array(16);
      window.crypto.getRandomValues(buf);
      id = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
}
function phoneKey(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  // Kenyan numbers written 07xx become 2547xx so the same person is one member.
  return digits.startsWith("0") && digits.length === 10 ? "254" + digits.slice(1) : digits;
}
const DEVICE_MSG = "This access code is already in use on another phone or computer. Each code works on one device. If you have changed your phone, contact the Imfuyo team and we will move it for you.";

/* ─── GLOBAL STYLES ───────────────────────────────────────── */
const globalCss = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes toastIn {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes wizardIn {
    from { opacity: 0; transform: translateY(8px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes pulseProjection {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`;

/* ─── TOAST ─────────────────────────────────────────────────── */
function Toast({ message, type = "success" }) {
  const bg = type === "error" ? "#dc2626" : "#0d1f16";
  const icon = type === "error"
    ? <X size={15} color="#fca5a5" />
    : <CheckCircle size={15} color="#3E7A32" />;
  return (
    <div style={{
      position: "fixed", bottom: "28px", left: "50%",
      transform: "translateX(-50%)",
      zIndex: 10000,
      background: bg, color: "#fff",
      padding: "12px 22px", borderRadius: "50px",
      fontSize: "14px", fontWeight: 600,
      fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
      display: "flex", alignItems: "center", gap: "8px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      whiteSpace: "nowrap",
      animation: "toastIn 0.4s cubic-bezier(0.16,1,0.3,1)",
    }}>
      {icon}
      {message}
    </div>
  );
}

/* ─── LOADING SPINNER ──────────────────────────────────────── */
function Spinner({ size = 17, color = "#fff" }) {
  return (
    <span style={{
      width: size, height: size,
      border: `2px solid rgba(255,255,255,0.25)`,
      borderTopColor: color,
      borderRadius: "50%",
      display: "inline-block",
      animation: "spin 0.8s linear infinite",
      flexShrink: 0,
    }} />
  );
}

/* ─── STAT CARD WITH PROJECTION ───────────────────────────── */
function StatCard({ icon: Icon, label, value, projection, isDark }) {
  const bg = isDark ? "#1f2937" : "#fff";
  const border = isDark ? "#374151" : "rgba(0,0,0,0.06)";
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#9ca3af" : "#6b7280";
  const iconBg = isDark ? "#14301A" : "#F1F5EE";

  return (
    <div style={{
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: "14px",
      padding: "18px 20px",
      flex: "1 1 180px",
      minWidth: "140px",
      transition: "transform 0.2s, box-shadow 0.2s",
      position: "relative",
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = isDark
        ? "0 8px 24px rgba(0,0,0,0.3)"
        : "0 8px 24px rgba(0,0,0,0.06)";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <div style={{
          width: "36px", height: "36px", borderRadius: "10px",
          background: iconBg,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon size={16} color="#3E7A32" />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: textMain, letterSpacing: "-0.02em" }}>
            {value}
          </p>
          <p style={{ margin: 0, fontSize: "11px", color: textSub, fontWeight: 500 }}>
            {label}
          </p>
        </div>
      </div>

      {/* Projection tooltip */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        marginTop: "6px",
        paddingTop: "6px",
        borderTop: `1px solid ${isDark ? "#374151" : "#f0f0ec"}`,
        animation: "pulseProjection 3s ease-in-out infinite",
      }}>
        <Target size={10} color="#3E7A32" style={{ flexShrink: 0 }} />
        <span style={{
          fontSize: "10px",
          color: isDark ? "#B7D9A8" : "#285020",
          fontWeight: 500,
          lineHeight: 1.3,
        }}>
          {projection}
        </span>
      </div>
    </div>
  );
}

/* ─── HOW IT WORKS WIZARD ──────────────────────────────────── */
function HowItWorksWizard({ isDark, accent = "#3E7A32" }) {
  const [open, setOpen]   = useState(false);
  const [step, setStep]   = useState(0);
  const ref               = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const total   = HOW_IT_WORKS.length;
  const current = HOW_IT_WORKS[step];

  const bg       = isDark ? "#1f2937" : "#fff";
  const border   = isDark ? "#374151" : "rgba(0,0,0,0.1)";
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub  = isDark ? "#9ca3af" : "#6b7280";

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => { setOpen(o => !o); setStep(0); }}
        title="How do I get access?"
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: isDark ? "#1f2937" : "#f4f4f2",
          border: `1.5px solid ${isDark ? "#374151" : "#ebe9e3"}`,
          borderRadius: "50px",
          padding: "7px 14px",
          fontSize: "12px", fontWeight: 600,
          color: isDark ? "#9ca3af" : "#6b7280",
          cursor: "pointer", fontFamily: "inherit",
          transition: "border-color 0.15s, color 0.15s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = accent;
          e.currentTarget.style.color = accent;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = isDark ? "#374151" : "#ebe9e3";
          e.currentTarget.style.color = isDark ? "#9ca3af" : "#6b7280";
        }}
      >
        <HelpCircle size={13} />
        How do I get access?
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 10px)",
            left: 0,
            zIndex: 9000,
            width: "300px",
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: "16px",
            boxShadow: isDark
              ? "0 16px 48px rgba(0,0,0,0.5)"
              : "0 16px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
            animation: "wizardIn 0.25s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <div style={{ height: "3px", background: isDark ? "#374151" : "#f0f0ec" }}>
            <div style={{
              height: "100%",
              width: `${((step + 1) / total) * 100}%`,
              background: accent,
              transition: "width 0.3s cubic-bezier(0.4,0,0.2,1)",
              borderRadius: "0 2px 2px 0",
            }} />
          </div>

          <div style={{ padding: "20px 20px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <span style={{
                fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: accent,
              }}>
                Step {step + 1} of {total}
              </span>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: textSub, display: "flex", padding: "2px",
                }}
              >
                <X size={13} />
              </button>
            </div>

            <div style={{
              width: "36px", height: "36px", borderRadius: "10px",
              background: isDark ? "#14301A" : "#F1F5EE",
              border: `1.5px solid ${isDark ? "#14301A" : "#D5E8CB"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: "12px",
            }}>
              <span style={{ fontSize: "14px", fontWeight: 800, color: accent }}>{current.n}</span>
            </div>

            <h4 style={{ margin: "0 0 6px", fontSize: "15px", fontWeight: 700, color: textMain, letterSpacing: "-0.01em" }}>
              {current.title}
            </h4>
            <p style={{ margin: 0, fontSize: "13px", color: textSub, lineHeight: 1.65 }}>
              {current.body}
            </p>
          </div>

          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 20px",
            borderTop: `1px solid ${isDark ? "#374151" : "#f0f0ec"}`,
          }}>
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              style={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                background: "none", border: "none", cursor: step === 0 ? "not-allowed" : "pointer",
                color: step === 0 ? (isDark ? "#374151" : "#d1d5db") : textSub,
                fontSize: "12px", fontWeight: 600, fontFamily: "inherit",
                padding: "4px 0", transition: "color 0.15s",
              }}
            >
              <ChevronLeft size={13} /> Prev
            </button>

            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              {HOW_IT_WORKS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  style={{
                    width: i === step ? "16px" : "6px",
                    height: "6px",
                    borderRadius: "3px",
                    background: i === step ? accent : (isDark ? "#374151" : "#e5e7eb"),
                    border: "none", cursor: "pointer", padding: 0,
                    transition: "width 0.25s, background 0.2s",
                  }}
                />
              ))}
            </div>

            {step < total - 1 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  background: "none", border: "none", cursor: "pointer",
                  color: accent, fontSize: "12px", fontWeight: 700,
                  fontFamily: "inherit", padding: "4px 0",
                }}
              >
                Next <ChevronRight size={13} />
              </button>
            ) : (
              <button
                onClick={() => setOpen(false)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  background: accent, border: "none", cursor: "pointer",
                  color: "#fff", fontSize: "12px", fontWeight: 700,
                  fontFamily: "inherit", padding: "5px 12px",
                  borderRadius: "50px",
                }}
              >
                Got it <Check size={11} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── ACCESS CODE MODAL ─────────────────────────────────────
   Replaces the old phone+PIN enroll/login modal. A single field:
   the access code staff handed the client. We look it up directly
   by document ID — no query, no scan.                          */
function AccessCodeModal({ onClose, onSuccess, isDark, accent = "#3E7A32", accentDark = "#285020" }) {
  const [code,     setCode]     = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  // Group keys: after the code is found, the member gives their name and phone.
  const [group,    setGroup]    = useState(null);   // { token, data }
  const [mName,    setMName]    = useState("");
  const [mPhone,   setMPhone]   = useState("");

  const bg       = isDark ? "#1f2937" : "#fff";
  const border   = isDark ? "#374151" : "rgba(0,0,0,0.1)";
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub  = isDark ? "#9ca3af" : "#6b7280";
  const labelCol = isDark ? "#d1d5db" : "#374151";
  const inputBg  = isDark ? "#111827" : "#fafaf8";

  const handleSubmit = async () => {
    const normalised = normaliseAccessCode(code);
    if (!normalised) {
      setError("Please enter your access code.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const ref = doc(db, "imfuyo_academy", normalised);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setError("We couldn't find an account for that code. Double-check it, or contact the Imfuyo team.");
        setLoading(false);
        return;
      }

      const data = snap.data();
      if (data.disabled) {
        setError("This access code has been switched off. Contact the Imfuyo team.");
        setLoading(false);
        return;
      }

      if (data.kind === "group") {
        // Step two: the member identifies themselves.
        setGroup({ token: normalised, data });
        setLoading(false);
        return;
      }

      const deviceId = getDeviceId();
      if (data.deviceId && deviceId && data.deviceId !== deviceId) {
        setError(DEVICE_MSG);
        setLoading(false);
        return;
      }

      const patch = { lastLogin: serverTimestamp() };
      if (!data.deviceId && deviceId) { patch.deviceId = deviceId; patch.deviceBoundAt = serverTimestamp(); }
      try {
        await updateDoc(ref, patch);
      } catch (e) {
        // If the database rules do not yet allow the device fields, still record the login.
        await updateDoc(ref, { lastLogin: serverTimestamp() });
      }

      onSuccess({
        token: normalised,
        fullName: data.fullName || "",
        phone: data.phone || "",
        programmes: data.programmes || [],
        paymentStatus: data.paymentStatus === "paid" ? "paid" : "unpaid",
      });
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please check your connection and try again.");
      setLoading(false);
    }
  };

  const handleGroupSubmit = async () => {
    const name = mName.trim();
    const key = phoneKey(mPhone);
    if (name.length < 2) { setError("Please enter your full name."); return; }
    if (key.length < 9) { setError("Please enter your phone number."); return; }
    setLoading(true);
    setError("");
    try {
      const { token, data } = group;
      const seats = Number(data.seats) || 5;
      const membersRef = collection(db, "imfuyo_academy", token, "members");
      const memberRef = doc(db, "imfuyo_academy", token, "members", key);
      const mSnap = await getDoc(memberRef);
      const deviceId = getDeviceId();

      if (mSnap.exists()) {
        const m = mSnap.data();
        if (m.deviceId && deviceId && m.deviceId !== deviceId) {
          setError(DEVICE_MSG);
          setLoading(false);
          return;
        }
        const patch = { lastLogin: serverTimestamp() };
        if (!m.deviceId && deviceId) { patch.deviceId = deviceId; patch.deviceBoundAt = serverTimestamp(); }
        await updateDoc(memberRef, patch);
      } else {
        const all = await getDocs(membersRef);
        if (all.size >= seats) {
          setError(`This group key is full (${seats} people). Ask your group leader or the Imfuyo team for more seats.`);
          setLoading(false);
          return;
        }
        await setDoc(memberRef, {
          fullName: name, phone: mPhone.trim(), deviceId: deviceId || null,
          deviceBoundAt: serverTimestamp(), joinedAt: serverTimestamp(), lastLogin: serverTimestamp(),
        });
      }
      await updateDoc(doc(db, "imfuyo_academy", token), { lastLogin: serverTimestamp() });

      onSuccess({
        token,
        pid: "m_" + key,
        fullName: mSnap.exists() ? (mSnap.data().fullName || name) : name,
        phone: mPhone.trim(),
        programmes: data.programmes || [],
        paymentStatus: data.paymentStatus === "paid" ? "paid" : "unpaid",
        groupName: data.groupName || data.fullName || "",
      });
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please check your connection and try again.");
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "0 14px", height: "50px", borderRadius: "10px",
    border: `1.5px solid ${isDark ? "#374151" : "rgba(0,0,0,0.12)"}`,
    fontSize: "16px", fontFamily: "inherit", outline: "none", boxSizing: "border-box",
    background: inputBg, color: textMain,
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(8px)",
      }}
    >
      <style>{globalCss}</style>
      <div style={{
        width: "min(420px,100%)",
        borderRadius: "20px",
        background: bg,
        boxShadow: isDark
          ? "0 24px 64px rgba(0,0,0,0.6)"
          : "0 24px 64px rgba(0,0,0,0.15)",
        overflow: "hidden",
        animation: "modalIn 0.3s cubic-bezier(0.16,1,0.3,1)",
      }}>
        {/* Top bar */}
        <div style={{ padding: "24px 24px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "18px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "11px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: textSub }}>
                Imfuyo Academy
              </p>
              <h3 style={{ margin: "4px 0 0", fontSize: "19px", fontWeight: 700, color: textMain, letterSpacing: "-0.02em" }}>
                Enter your access code
              </h3>
            </div>
            <button onClick={onClose} style={{
              background: isDark ? "#374151" : "#f4f4f2",
              border: "none", cursor: "pointer", borderRadius: "8px",
              padding: "6px", display: "flex", alignItems: "center",
              color: textSub,
            }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "4px 24px 24px" }}>
          <p style={{ margin: "0 0 18px", fontSize: "13px", color: textSub, lineHeight: 1.6 }}>
            {group
              ? `Group key ${group.token}${group.data.groupName ? ` for ${group.data.groupName}` : ""}. Tell us who you are so the course keeps your own progress and prints your own certificate.`
              : "Use the access code the Imfuyo team gave you when they set up your account. Each code works on one phone or computer."}
          </p>

          {error && (
            <div style={{
              background: isDark ? "#450a0a" : "#fef2f2",
              border: "1.5px solid #fecaca",
              borderRadius: "10px", padding: "12px",
              marginBottom: "14px", fontSize: "13px",
              color: isDark ? "#fca5a5" : "#dc2626",
              lineHeight: 1.5,
              display: "flex", gap: "8px", alignItems: "flex-start",
            }}>
              <AlertCircle size={15} style={{ flexShrink: 0, marginTop: "1px" }} />
              <span>{error}</span>
            </div>
          )}

          {group ? (
            <>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: labelCol, marginBottom: "6px" }}>Your full name <span style={{ color: "#ef4444" }}>*</span></label>
              <input type="text" value={mName} onChange={(e) => { setMName(e.target.value); setError(""); }} placeholder="Jane Wanjiru" autoFocus style={inputStyle} />
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: labelCol, margin: "14px 0 6px" }}>Your phone number <span style={{ color: "#ef4444" }}>*</span></label>
              <input type="tel" value={mPhone} onChange={(e) => { setMPhone(e.target.value); setError(""); }} onKeyDown={(e) => { if (e.key === "Enter") handleGroupSubmit(); }} placeholder="07XX XXX XXX" style={inputStyle} />
              <button onClick={handleGroupSubmit} disabled={loading} style={{
                marginTop: "18px", width: "100%", padding: "14px", borderRadius: "12px", border: "none",
                background: loading ? "#d1fae5" : `linear-gradient(135deg, ${accentDark}, ${accent})`,
                color: loading ? "#6b7280" : "#fff", fontSize: "15px", fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}>
                {loading ? <><Spinner size={16} color="#285020" /> Checking…</> : <>Enter Course <ArrowRight size={16} /></>}
              </button>
              <p style={{ textAlign: "center", fontSize: "12px", color: textSub, margin: "16px 0 0", lineHeight: 1.6 }}>
                Use the same name and phone number each time. <button onClick={() => { setGroup(null); setError(""); }} style={{ background: "none", border: "none", color: accent, cursor: "pointer", fontWeight: 700, fontFamily: "inherit", padding: 0 }}>Use a different code</button>
              </p>
            </>
          ) : (
            <>
          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: labelCol, marginBottom: "6px" }}>
            Access Code <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <KeyRound size={15} color={error ? "#ef4444" : "#9ca3af"}
              style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="IMF-4K7X-2Q9P"
              autoFocus
              style={{
                width: "100%",
                padding: "0 14px 0 44px",
                height: "50px",
                borderRadius: "10px",
                border: `1.5px solid ${error ? "#ef4444" : isDark ? "#374151" : "rgba(0,0,0,0.12)"}`,
                fontSize: "16px",
                fontWeight: 600,
                letterSpacing: "0.04em",
                fontFamily: "inherit",
                outline: "none",
                boxSizing: "border-box",
                background: inputBg,
                color: textMain,
                textTransform: "uppercase",
                transition: "border-color 0.15s",
              }}
              onFocus={e => e.target.style.borderColor = accent}
              onBlur={e => e.target.style.borderColor = error ? "#ef4444" : (isDark ? "#374151" : "rgba(0,0,0,0.12)")}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              marginTop: "18px", width: "100%", padding: "14px",
              borderRadius: "12px", border: "none",
              background: loading ? "#d1fae5"
                : `linear-gradient(135deg, ${accentDark}, ${accent})`,
              color: loading ? "#6b7280" : "#fff",
              fontSize: "15px", fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              boxShadow: loading ? "none" : `0 4px 20px ${accent}40`,
            }}
          >
            {loading
              ? <><Spinner size={16} color="#285020" /> Checking…</>
              : <>Enter Course <ArrowRight size={16} /></>
            }
          </button>

          <p style={{ textAlign: "center", fontSize: "12px", color: textSub, margin: "16px 0 0", lineHeight: 1.6 }}>
            Don't have a code yet? <a href="/shop/products/mbuziplus-course" style={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}>Register and pay in the Marketplace</a> and we will send your code on WhatsApp.
          </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── DASHBOARD BANNER (logged in) ──────────────────────────── */
function DashboardBanner({ user, programmes, onLogout, onOpenCourse, isDark }) {
  const enrolledPrograms = programmes.filter(p => user.programmes?.includes(p.id));
  const isPaid = user.paymentStatus === "paid";

  const bg      = isDark ? "#14301A" : "#F1F5EE";
  const border  = isDark ? "#14301A" : "#D5E8CB";
  const text    = isDark ? "#B7D9A8" : "#14301A";
  const subText = isDark ? "#8DBB7E" : "#4b7a62";

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto 16px", padding: "0 32px" }}>
      <div style={{
        background: bg,
        border: `1.5px solid ${border}`,
        borderRadius: "16px",
        padding: "18px 24px",
        display: "flex", flexWrap: "wrap",
        alignItems: "center", justifyContent: "space-between", gap: "14px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "40px", height: "40px", borderRadius: "50%",
            background: isDark ? "#14301A" : "#E3EEDD",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <BookMarked size={18} color={isDark ? "#B7D9A8" : "#285020"} />
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", color: isDark ? "#f9fafb" : "#0d1f16" }}>
              Welcome back, {user.fullName || user.phone}
            </p>
            <p style={{ margin: 0, fontSize: "12px", color: subText }}>
              {enrolledPrograms.length
                ? `Enrolled in: ${enrolledPrograms.map(p => p.title).join(", ")}`
                : "No programmes on this account yet."}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "6px 12px", borderRadius: "50px",
            fontSize: "12px", fontWeight: 700,
            color: isPaid ? (isDark ? "#8DBB7E" : "#285020") : (isDark ? "#fbbf24" : "#b45309"),
            background: isPaid ? (isDark ? "#14301A" : "#E3EEDD") : (isDark ? "#2a1f0a" : "#fef3c7"),
            border: `1px solid ${isPaid ? (isDark ? "#14301A" : "#D5E8CB") : (isDark ? "#92400e" : "#fde68a")}`,
          }}>
            <CreditCard size={12} /> {isPaid ? "Payment Confirmed" : "Payment Pending"}
          </span>
          {enrolledPrograms.find(p => p.id === "mbuziplus") && (
            <button
              onClick={() => onOpenCourse("mbuziplus")}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                background: "#285020", color: "#fff",
                border: "none", borderRadius: "50px",
                padding: "8px 16px", fontSize: "13px", fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              <Play size={13} /> Continue MbuziPlus
            </button>
          )}
          <button
            onClick={onLogout}
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              background: "transparent", color: text,
              border: `1.5px solid ${border}`, borderRadius: "50px",
              padding: "8px 16px", fontSize: "13px", fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>

      {!isPaid && (
        <div style={{
          marginTop: "10px",
          display: "flex", alignItems: "center", gap: "8px",
          padding: "10px 16px",
          background: isDark ? "#2a1f0a" : "#fffbeb",
          border: `1px solid ${isDark ? "#92400e" : "#fde68a"}`,
          borderRadius: "10px",
        }}>
          <AlertCircle size={14} color={isDark ? "#fbbf24" : "#b45309"} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: "12px", color: isDark ? "#fbbf24" : "#92400e", lineHeight: 1.5 }}>
            Your payment is still pending — you can keep learning in the meantime, but please confirm payment with the Imfuyo team soon.
          </span>
        </div>
      )}
    </div>
  );
}

/* ─── PROGRAM CARD ───────────────────────────────────────────── */
function ProgramCard({ prog, onAccess, onPreview, isEnrolled, isDark }) {
  const live = prog.status === "enrolling";
  const c = isDark
    ? { card: "#16251b", ink: "#f2f6ee", sub: "#a9bfae", line: "#243629", chip: "#1a2c20" }
    : { card: "#ffffff", ink: "#14301a", sub: "#4f6654", line: "#e2dccb", chip: "#eef3e8" };
  const btn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", borderRadius: 999, padding: "13px 22px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
  return (
    <article style={{ background: c.card, border: `1px solid ${c.line}`, borderRadius: 24, overflow: "hidden", display: "flex", flexDirection: "column", minWidth: 0, opacity: live ? 1 : 0.92 }}>
      {live && <img src="/academy/couple-goats.jpg" alt="A couple with their two dairy goats" loading="lazy" style={{ display: "block", width: "100%", height: 420, objectFit: "cover", objectPosition: "50% 20%" }} />}
      <div style={{ background: live ? "linear-gradient(135deg,#1f4d22,#14301a)" : "linear-gradient(135deg,#1d4ed8,#0e2a63)", color: "#fff", padding: "28px 24px", display: "flex", alignItems: "center", gap: 14 }}>
        {live ? <BookOpen size={36} /> : <GraduationCap size={36} />}
        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>{live ? "Imfuyo Academy" : "Imfuyo Academy · Later"}</span>
      </div>
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <span style={{ alignSelf: "flex-start", fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 999, background: live ? "#3E7A32" : c.chip, color: live ? "#fff" : c.sub }}>{prog.tag}</span>
        <div>
          <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: c.ink }}>{prog.title}</h3>
          <div style={{ fontSize: 14.5, color: c.sub, marginTop: 2 }}>{prog.subtitle}</div>
        </div>
        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.6, color: c.sub }}>{prog.description}</p>
        {prog.facts.length > 0 && (
          <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
            {prog.facts.map(([n, l]) => (
              <div key={l}><div style={{ fontSize: 28, fontWeight: 800, color: "#3E7A32", lineHeight: 1 }}>{n}</div><div style={{ fontSize: 12.5, color: c.sub }}>{l}</div></div>
            ))}
          </div>
        )}
        {prog.modules.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {prog.modules.map((m) => <span key={m} style={{ fontSize: 12.5, fontWeight: 600, padding: "5px 11px", borderRadius: 999, background: c.chip, color: c.ink }}>{m}</span>)}
          </div>
        )}
        <div style={{ marginTop: "auto", paddingTop: 8, display: "flex", flexWrap: "wrap", gap: 10 }}>
          {live ? (
            <>
              <button onClick={() => onAccess(prog)} style={{ ...btn, background: "#D9601A", color: "#fff" }}>{isEnrolled ? "Continue learning" : "I have a code"} <ArrowRight size={17} /></button>
              <button onClick={onPreview} style={{ ...btn, background: "transparent", color: c.ink, boxShadow: `inset 0 0 0 2px ${c.ink}` }}>Try it free</button>
            </>
          ) : (
            <a href={NOTIFY_URL} target="_blank" rel="noopener noreferrer" style={{ ...btn, background: "#1d4ed8", color: "#fff", textDecoration: "none" }}>Tell me when it opens</a>
          )}
        </div>
      </div>
    </article>
  );
}

/* ─── MAIN COMPONENT ────────────────────────────────────────── */
// One tap on "Try it free": scroll to the free lesson and start it.
const startFreePreview = () => {
  document.getElementById("academy-preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
  try { window.dispatchEvent(new Event("imfuyo-start-preview")); } catch (e) { /* ignore */ }
};

export default function ImfuyoAcademy({ isDark = false, onBack, onNavigate }) {
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [toast,     setToast]     = useState(null);
  const [user,      setUser]      = useState(null);
  // "academy" = landing/catalogue view, "mbuziplus" = inside the MbuziPlus course
  const [view,      setView]      = useState("academy");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3400);
  };

  const handleAccessSuccess = (accountData) => {
    setShowAccessModal(false);
    setUser(accountData);
    showToast(`Welcome back, ${accountData.fullName || "friend"}!`);

    if (accountData.programmes?.includes("mbuziplus")) {
      setView("mbuziplus");
    }
  };

  const handleLogout = () => {
    setUser(null);
    setView("academy");
    showToast("Signed out successfully.");
  };

  const enrolledIds = user?.programmes || [];

  /* ─── COURSE VIEW: MbuziPlus ─── */
  if (view === "mbuziplus") {
    return (
      <MbuziplusCourse
        isDark={isDark}
        user={user}
        onBack={() => setView("academy")}
        onLogout={handleLogout}
      />
    );
  }

  const bg          = isDark ? "#0e1a12" : "#f3efe4";
  const textMain    = isDark ? "#f9fafb" : "#0d1f16";
  const textSub     = isDark ? "#9ca3af" : "#6b7280";
  const dividerCol  = isDark ? "#374151" : "#ebe9e3";

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", paddingTop: "80px" }}>
      <style>{globalCss}</style>

      {/* ─── LANDING: hero, facts, journey, MbuziPlus link, what you get ─── */}
      <AcademyLanding
        isDark={isDark}
        onEnterCode={() => setShowAccessModal(true)}
        onEnrol={openRegister}
        onPreview={() => startFreePreview()}
        afterHero={(
          <>
            <div id="academy-preview" style={{ paddingTop: "24px" }} />
            <AcademyTeaser
              isDark={isDark}
              onEnrol={openRegister}
              onEnterCode={() => setShowAccessModal(true)}
            />
          </>
        )}
      />

      {/* ─── LOGGED IN DASHBOARD BANNER ─── */}
      {user && (
        <div style={{ paddingTop: "32px" }}>
          <DashboardBanner
            user={user}
            programmes={PROGRAMS}
            onLogout={handleLogout}
            onOpenCourse={(id) => { if (id === "mbuziplus") setView("mbuziplus"); }}
            isDark={isDark}
          />
        </div>
      )}

      {/* ─── SAMPLE CERTIFICATE ─── */}
      <section aria-labelledby="sample-cert-title" style={{ maxWidth: "1100px", margin: "0 auto", padding: "8px clamp(16px, 3vw, 32px) 56px" }}>
        <div style={{ textAlign: "center", marginBottom: "22px" }}>
          <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#3E7A32" }}>Your recognition</p>
          <h2 id="sample-cert-title" style={{ margin: "0 0 10px", fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "clamp(24px, 4vw, 34px)", color: textMain, letterSpacing: "-0.02em" }}>
            Graduate and earn your certificate
          </h2>
          <p style={{ margin: "0 auto", maxWidth: "620px", fontSize: "15px", lineHeight: 1.6, color: isDark ? "#9ca3af" : "#5a5a52" }}>
            Every MbuziPlus Student who completes the MbuziPlus Course by Imfuyo Academy receives a Certificate of Completion with their own name, signed by the Founder and Chief Executive Officer of Imfuyo.
          </p>
        </div>
        <img
          src="/certificate/sample-certificate.jpg"
          alt="Sample Certificate of Completion for the MbuziPlus Course by Imfuyo Academy"
          loading="lazy"
          style={{ display: "block", width: "100%", maxWidth: "820px", margin: "0 auto", borderRadius: "10px", boxShadow: "0 18px 50px rgba(20,60,20,0.22)", border: isDark ? "1px solid #223328" : "1px solid #e2efe2" }}
        />
        <p style={{ margin: "12px 0 0", textAlign: "center", fontSize: "12.5px", color: isDark ? "#7a8a7c" : "#7a7a70" }}>Sample certificate. Yours will carry your name.</p>
      </section>

      {/* ─── DIVIDER ─── */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 32px 32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ flex: 1, height: "1px", background: dividerCol }} />
          <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: isDark ? "#4b5563" : "#c4c4bc" }}>Programmes at Imfuyo Academy</span>
          <div style={{ flex: 1, height: "1px", background: dividerCol }} />
        </div>
      </div>

      {/* ─── PROGRAMS GRID ─── */}
      <section style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 clamp(16px,3vw,32px) 24px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 440px), 1fr))",
          gap: "20px", alignItems: "start",
        }}>
          {PROGRAMS.map(prog => (
            <ProgramCard
              key={prog.id}
              prog={prog}
              onAccess={(p) => {
                // Already enrolled in this programme? Skip the modal, go straight in.
                if (enrolledIds.includes(p.id) && p.id === "mbuziplus") {
                  setView("mbuziplus");
                  return;
                }
                setShowAccessModal(true);
              }}
              onPreview={() => startFreePreview()}
              isEnrolled={enrolledIds.includes(prog.id)}
              isDark={isDark}
            />
          ))}
        </div>
      </section>

      {/* ─── HOW TO JOIN + QUESTIONS ─── */}
      <AcademyJoin
        isDark={isDark}
        onEnterCode={() => setShowAccessModal(true)}
        onEnrol={openRegister}
      />

      {/* ─── ACCESS CODE MODAL ─── */}
      {showAccessModal && (
        <AccessCodeModal
          onClose={() => setShowAccessModal(false)}
          onSuccess={handleAccessSuccess}
          isDark={isDark}
          accent="#3E7A32"
          accentDark="#285020"
        />
      )}

      {/* ─── TOAST ─── */}
      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}